import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from './Modal.jsx';
import FormField from './FormField.jsx';
import { useCreateEntity, useUpdateEntity } from '../hooks/useAdminEntities.js';
import { useEntityMembers } from '../hooks/useEntityMembers.js';
import { ApiError } from '../lib/api.js';

const SLUG_REGEX = /^[a-z][a-z0-9_-]{1,30}$/;

const EMPTY_FORM = {
  slug: '',
  name: '',
  legal_name: '',
  siret: '',
  vat_number: '',
  country: 'FR',
  currency: 'EUR',
  status: 'active',
  include_in_group: false,
};

function validate(fields, isEdit) {
  const errors = {};
  if (!isEdit && !SLUG_REGEX.test(fields.slug)) {
    errors.slug = 'Slug invalide (a-z, chiffres, _ ou -, 2–31 chars, commence par une lettre)';
  }
  if (!fields.name.trim()) {
    errors.name = 'Nom requis';
  }
  if (fields.siret && fields.siret.replace(/\s/g, '').length !== 14) {
    errors.siret = 'Le SIRET doit contenir 14 chiffres';
  }
  if (fields.country && fields.country.length !== 2) {
    errors.country = 'Code pays sur 2 caractères (ex. FR)';
  }
  if (fields.currency && fields.currency.length !== 3) {
    errors.currency = 'Code devise sur 3 caractères (ex. EUR)';
  }
  return errors;
}

export default function EntityFormModal({ open, onClose, entity }) {
  const isEdit = Boolean(entity);
  const createEntity = useCreateEntity();
  const updateEntity = useUpdateEntity();
  // Reuses the members cache populated by the card's ShareBadge/EntitySharePanel
  // (same queryKey) — no extra request in the common case where the card is
  // already mounted. Only relevant in edit mode: a not-yet-created entity has
  // no members query to run.
  const { data: currentMembers } = useEntityMembers(isEdit ? entity?.id : null);

  const [fields, setFields] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (open) {
      setFields(
        entity
          ? {
              slug: entity.slug ?? '',
              name: entity.name ?? '',
              legal_name: entity.legal_name ?? '',
              siret: entity.siret ?? '',
              vat_number: entity.vat_number ?? '',
              country: entity.country ?? 'FR',
              currency: entity.currency ?? 'EUR',
              status: entity.status ?? 'active',
              include_in_group: entity.include_in_group ?? false,
            }
          : EMPTY_FORM
      );
      setErrors({});
      setServerError(null);
    }
  }, [open, entity]);

  // A brand-new entity always starts private (only its creator is a member),
  // same as an existing entity where owner_user_id is set and member_count === 1.
  const isCurrentlyPrivate = isEdit
    ? Boolean(entity?.owner_user_id) && (currentMembers?.length ?? 1) === 1
    : true;
  const showGroupWarning = isCurrentlyPrivate && fields.include_in_group;

  function set(key, value) {
    setFields((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError(null);

    const validationErrors = validate(fields, isEdit);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      name: fields.name.trim(),
      legal_name: fields.legal_name.trim() || undefined,
      siret: fields.siret.replace(/\s/g, '') || undefined,
      vat_number: fields.vat_number.trim() || undefined,
      country: fields.country.toUpperCase() || undefined,
      currency: fields.currency.toUpperCase() || undefined,
      include_in_group: fields.include_in_group,
    };

    try {
      if (isEdit) {
        await updateEntity.mutateAsync({ id: entity.id, ...payload, status: fields.status });
      } else {
        await createEntity.mutateAsync({ slug: fields.slug, ...payload });
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrors((prev) => ({ ...prev, slug: 'Ce slug est déjà utilisé' }));
      } else {
        setServerError(err.message ?? 'Erreur lors de la sauvegarde');
      }
    }
  }

  const isPending = createEntity.isPending || updateEntity.isPending;

  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-50 disabled:text-slate-400';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Modifier l'entité` : 'Nouvelle entité'}
      size="md"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Slug */}
        <FormField
          label="Slug"
          htmlFor="entity-slug"
          required={!isEdit}
          error={errors.slug}
          hint="Identifiant unique, immuable après création (ex. legendary-parts)"
        >
          <input
            id="entity-slug"
            type="text"
            className={inputClass}
            value={fields.slug}
            onChange={(e) => set('slug', e.target.value.toLowerCase())}
            disabled={isEdit}
            placeholder="legendary-parts"
            autoComplete="off"
          />
        </FormField>

        {/* Nom */}
        <FormField label="Nom" htmlFor="entity-name" required error={errors.name}>
          <input
            id="entity-name"
            type="text"
            className={inputClass}
            value={fields.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Legendary Parts"
          />
        </FormField>

        {/* Raison sociale */}
        <FormField label="Raison sociale" htmlFor="entity-legal-name" error={errors.legal_name}>
          <input
            id="entity-legal-name"
            type="text"
            className={inputClass}
            value={fields.legal_name}
            onChange={(e) => set('legal_name', e.target.value)}
            placeholder="Legendary Parts SAS"
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* SIRET */}
          <FormField label="SIRET" htmlFor="entity-siret" error={errors.siret}>
            <input
              id="entity-siret"
              type="text"
              inputMode="numeric"
              className={inputClass}
              value={fields.siret}
              onChange={(e) => set('siret', e.target.value)}
              placeholder="12345678901234"
              maxLength={14}
            />
          </FormField>

          {/* N° TVA */}
          <FormField label="N° TVA intracommunautaire" htmlFor="entity-vat" error={errors.vat_number}>
            <input
              id="entity-vat"
              type="text"
              className={inputClass}
              value={fields.vat_number}
              onChange={(e) => set('vat_number', e.target.value)}
              placeholder="FR12345678901"
            />
          </FormField>

          {/* Pays */}
          <FormField label="Pays" htmlFor="entity-country" error={errors.country}>
            <input
              id="entity-country"
              type="text"
              className={inputClass}
              value={fields.country}
              onChange={(e) => set('country', e.target.value.toUpperCase())}
              placeholder="FR"
              maxLength={2}
            />
          </FormField>

          {/* Devise */}
          <FormField label="Devise" htmlFor="entity-currency" error={errors.currency}>
            <input
              id="entity-currency"
              type="text"
              className={inputClass}
              value={fields.currency}
              onChange={(e) => set('currency', e.target.value.toUpperCase())}
              placeholder="EUR"
              maxLength={3}
            />
          </FormField>
        </div>

        {/* Statut (edit only) */}
        {isEdit && (
          <FormField label="Statut" htmlFor="entity-status" error={errors.status}>
            <select
              id="entity-status"
              className={inputClass}
              value={fields.status}
              onChange={(e) => set('status', e.target.value)}
            >
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
              <option value="archived">Archivé</option>
            </select>
          </FormField>
        )}

        {/* Vue groupe / refacturation */}
        <div>
          <label htmlFor="entity-include-in-group" className="flex items-start gap-2 text-sm text-slate-700">
            <input
              id="entity-include-in-group"
              type="checkbox"
              checked={fields.include_in_group}
              onChange={(e) => set('include_in_group', e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-slate-800 focus:ring-slate-500"
            />
            <span>Inclure dans la vue groupe et la refacturation</span>
          </label>

          {showGroupWarning && (
            <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-orange-50 px-3 py-2 text-xs text-orange-700">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden />
              Cette société entrera dans les consolidations de groupe.
            </p>
          )}
        </div>

        {serverError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {serverError}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {isPending ? 'Enregistrement...' : isEdit ? 'Sauvegarder' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
